import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

import { StudentRegister } from '../interfaces/register.interface';
import { PaymentMaibRequest, PaymentMaibResponse } from '../interfaces/payment-maib.interface';
import { ContactsFormInterface } from '../interfaces/contacts-form.interface';
import { ConsultationForm } from '../interfaces/consultation.interface';
import { ReviewForm } from '../interfaces/reviews.interface';

type RequestParams = Record<string, any> | HttpParams | undefined | null;

export interface RequestCacheOptions {
  cache?: boolean;
  forceRefresh?: boolean;
  ttlMs?: number;
}

interface CacheEntry<T> {
  response$: Observable<T>;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class PublicService {
  private readonly cache = new Map<string, CacheEntry<any>>();
  private readonly cacheTtlMs = 5 * 60 * 1000;

  constructor(private http: HttpClient) {}

  private sortDeep(value: any): any {
    if (Array.isArray(value)) {
      return value.map(item => this.sortDeep(item));
    }

    if (value && typeof value === 'object') {
      return Object.keys(value)
        .sort()
        .reduce((acc: any, key) => {
          acc[key] = this.sortDeep(value[key]);
          return acc;
        }, {});
    }

    return value;
  }

  private buildHttpParams(params?: RequestParams): HttpParams | undefined {
    if (!params) return undefined;
    if (params instanceof HttpParams) return params;

    let httpParams = new HttpParams();

    Object.keys(params)
      .sort()
      .forEach((key) => {
        const value = params[key];

        if (value === null || value === undefined || value === '') {
          return;
        }

        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== null && item !== undefined && item !== '') {
              httpParams = httpParams.append(key, String(item));
            }
          });
          return;
        }

        if (typeof value === 'object') {
          httpParams = httpParams.set(key, JSON.stringify(this.sortDeep(value)));
          return;
        }

        httpParams = httpParams.set(key, String(value));
      });

    return httpParams;
  }

  private buildCacheKey(url: string, params?: RequestParams): string {
    const httpParams = this.buildHttpParams(params);
    const query = httpParams?.toString() ?? '';
    return query ? `${url}?${query}` : url;
  }

  private rawGet<T>(url: string, params?: RequestParams): Observable<T> {
    const httpParams = this.buildHttpParams(params);
    return this.http.get<T>(url, { params: httpParams });
  }

  private getFromCache<T>(key: string): Observable<T> | null {
    const cached = this.cache.get(key);

    if (!cached) return null;

    if (Date.now() > cached.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return cached.response$ as Observable<T>;
  }

  private cachedGet<T>(
    url: string,
    params?: RequestParams,
    options?: RequestCacheOptions
  ): Observable<T> {
    const useCache = options?.cache !== false;

    if (!useCache) {
      return this.rawGet<T>(url, params);
    }

    const key = this.buildCacheKey(url, params);

    if (options?.forceRefresh) {
      this.cache.delete(key);
    } else {
      const cached = this.getFromCache<T>(key);
      if (cached) return cached;
    }

    const request$ = this.rawGet<T>(url, params).pipe(
      shareReplay({
        bufferSize: 1,
        refCount: false
      }),
      catchError((error) => {
        this.cache.delete(key);
        return throwError(() => error);
      })
    );

    this.cache.set(key, {
      response$: request$,
      expiresAt: Date.now() + (options?.ttlMs ?? this.cacheTtlMs)
    });

    return request$;
  }

  clearCache(): void {
    this.cache.clear();
  }

  invalidateUrl(url: string): void {
    for (const key of this.cache.keys()) {
      if (key === url || key.startsWith(`${url}?`)) {
        this.cache.delete(key);
      }
    }
  }

  invalidateExact(url: string, params?: RequestParams): void {
    const key = this.buildCacheKey(url, params);
    this.cache.delete(key);
  }

  getDataByTransferStateUrl<T = unknown>(
    url: string,
    params?: RequestParams,
    options?: RequestCacheOptions
  ): Observable<T> {
    return this.cachedGet<T>(url, params, options);
  }

  // PSP Maib
  payMaib(params: PaymentMaibRequest): Observable<PaymentMaibResponse> {
    const url = 'api/public/payment/maib/pay';
    return this.http.post<PaymentMaibResponse>(url, params);
  }

  payMaibDetails(payId: string): Observable<any> {
    return this.http.get(`api/public/payment/maib/details/${payId}`);
  }

  sendCourseRegister(register: StudentRegister): Observable<any> {
    const { name, email } = register;
    return this.http.post<any>('api/public/crud/registers', { data: [{ name }, { email }] });
  }

  sendContacts(contacts: ContactsFormInterface): Observable<any> {
    return this.http.post<any>('api/contacts', contacts);
  }

  sendConsultations(consultation: ConsultationForm): Observable<any> {
    return this.http.post<any>('api/consultations', consultation);
  }

  sendReviews(review: ReviewForm): Observable<any> {
    return this.http.post<any>('api/reviews', review);
  }

  getCompanyDetails(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/company-details', params, options);
  }

  getLanguages(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/public/languages', params, options);
  }

  getNetworks(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/networks', params, options);
  }

  getAbout(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/about', params, options);
  }

  getServices(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/services/active', params, options);
  }

  getServiceById(id: number | string, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>(`api/services/active/${id}`, undefined, options);
  }

  getBlogs(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/blogs', params, options);
  }

  getBlogById(id: number | string, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>(`api/blogs/${id}`, undefined, options);
  }

  getGuides(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/guides', params, options);
  }

  getGuideById(id: number | string, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>(`api/guides/${id}`, undefined, options);
  }

  getSpeakers(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/speakers', params, options);
  }

  getInstagramPosts(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/instagram-posts', params, options);
  }

  getEvents(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/events', params, options);
  }

  getEventById(id: number | string, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>(`api/events/active/${id}`, undefined, options);
  }

  getBooks(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/books', params, options);
  }

  getCourses(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/course-category/active', params, options);
  }

  getCourseById(id: number | string, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>(`api/course-category/active/${id}`, undefined, options);
  }

  getStaticPages(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/static-pages', params, options);
  }

  getAnalytics(options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/analytics', undefined, options);
  }

  getPackages(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/packages/active', params, options);
  }

  getPackageById(id: number | string, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>(`api/packages/active/${id}`, undefined, options);
  }

  getReviews(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/reviews', params, options);
  }

  getCertifications(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/certifications', params, options);
  }

  getPodcasts(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/podcasts', params, options);
  }

  getMeta(params?: any, options?: RequestCacheOptions): Observable<any> {
    return this.cachedGet<any>('api/meta', params, options);
  }

  downloadFileBlob(id: number): Observable<Blob> {
    return this.http.get(`api/files/${id}/download`, { responseType: 'blob' });
  }
}

export function resolveDownloadName(fileName: string | undefined | null): string {
  if (!fileName) {
    return 'download';
  }
  const base = fileName.split('/').pop() || fileName;
  return base.replace(/^\d+_/, '') || 'download';
}
